import { offlineDbHealth, OFFLINE_DB_VERSION, putRecord } from './db';
import { getDeviceId } from './device';
import type { StorageHealthState, StoragePersistenceState } from './types';

export interface StorageSafetySnapshot {
  device_id: string;
  persistence_state: StoragePersistenceState;
  usage_bytes: number | null;
  quota_bytes: number | null;
  remaining_bytes: number | null;
  usage_ratio: number | null;
  db_version: number;
  db_health: ReturnType<typeof offlineDbHealth>['state'];
  state: StorageHealthState;
  checked_at: string;
}

function classify(usage: number | null, quota: number | null, dbState: ReturnType<typeof offlineDbHealth>['state']): StorageHealthState {
  if (dbState === 'CORRUPT' || dbState === 'RECOVERY_REQUIRED') return 'CRITICAL';
  if (dbState === 'DEGRADED') return 'WATCH';
  if (usage === null || quota === null || quota <= 0) return 'UNKNOWN';
  const ratio = usage / quota;
  const remaining = quota - usage;
  if (ratio >= 0.90 || remaining < 50 * 1024 * 1024) return 'CRITICAL';
  if (ratio >= 0.80 || remaining < 150 * 1024 * 1024) return 'LOW';
  if (ratio >= 0.65 || remaining < 300 * 1024 * 1024) return 'WATCH';
  return 'HEALTHY';
}

export async function requestStoragePersistence(): Promise<StoragePersistenceState> {
  const storage = navigator.storage;
  if (!storage || typeof storage.persisted !== 'function') return 'UNSUPPORTED';
  try {
    if (await storage.persisted()) return 'PERSISTENT';
    if (typeof storage.persist !== 'function') return 'BEST_EFFORT';
    return (await storage.persist()) ? 'PERSISTENT' : 'BEST_EFFORT';
  } catch {
    return 'UNKNOWN';
  }
}

export async function inspectStorageSafety(requestPersistence = false): Promise<StorageSafetySnapshot> {
  const storage = navigator.storage;
  let persistence: StoragePersistenceState = 'UNKNOWN';
  if (storage && typeof storage.persisted === 'function') {
    try {
      persistence = (await storage.persisted()) ? 'PERSISTENT' : 'BEST_EFFORT';
      if (requestPersistence && persistence !== 'PERSISTENT') persistence = await requestStoragePersistence();
    } catch { persistence = 'UNKNOWN'; }
  } else {
    persistence = 'UNSUPPORTED';
  }

  let usage: number | null = null;
  let quota: number | null = null;
  if (storage && typeof storage.estimate === 'function') {
    try {
      const estimate = await storage.estimate();
      usage = Number.isFinite(estimate.usage as number) ? Number(estimate.usage) : null;
      quota = Number.isFinite(estimate.quota as number) ? Number(estimate.quota) : null;
    } catch { /* reported as unknown below */ }
  }
  const remaining = usage !== null && quota !== null ? Math.max(0, quota - usage) : null;
  const ratio = usage !== null && quota !== null && quota > 0 ? usage / quota : null;
  const health = offlineDbHealth();
  const snapshot: StorageSafetySnapshot = {
    device_id: getDeviceId(),
    persistence_state: persistence,
    usage_bytes: usage,
    quota_bytes: quota,
    remaining_bytes: remaining,
    usage_ratio: ratio,
    db_version: OFFLINE_DB_VERSION,
    db_health: health.state,
    state: classify(usage, quota, health.state),
    checked_at: new Date().toISOString(),
  };
  try { await putRecord('device_state', { device_id: snapshot.device_id, storage: snapshot, updated_at: snapshot.checked_at }); } catch { /* diagnostics must not hide the original storage state */ }
  return snapshot;
}

export async function preflightCriticalSession(expectedBytes = 25 * 1024 * 1024): Promise<{ outcome: 'READY' | 'READY_WITH_WARNINGS' | 'BLOCKED'; snapshot: StorageSafetySnapshot; reasons: string[] }> {
  const snapshot = await inspectStorageSafety(true);
  const reasons: string[] = [];
  if (snapshot.db_health === 'CORRUPT' || snapshot.db_health === 'RECOVERY_REQUIRED') reasons.push(`Local database health is ${snapshot.db_health}`);
  if (snapshot.remaining_bytes !== null && snapshot.remaining_bytes < expectedBytes) reasons.push('Estimated remaining browser storage is below the expected session size');
  if (snapshot.state === 'CRITICAL') reasons.push('Browser storage is in a critical-risk state');
  if (snapshot.persistence_state === 'BEST_EFFORT') reasons.push('Browser denied persistent-storage protection; local data remains best-effort');
  if (snapshot.persistence_state === 'UNSUPPORTED' || snapshot.persistence_state === 'UNKNOWN') reasons.push('Browser storage persistence status cannot be verified');

  const blocked = snapshot.state === 'CRITICAL' || snapshot.db_health === 'CORRUPT' || snapshot.db_health === 'RECOVERY_REQUIRED' || (snapshot.remaining_bytes !== null && snapshot.remaining_bytes < expectedBytes);
  return { outcome: blocked ? 'BLOCKED' : reasons.length ? 'READY_WITH_WARNINGS' : 'READY', snapshot, reasons };
}
